import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { list_my_requests, update_template_request_status } from '@/api/template-requests';
import { TemplateRequest, TemplateRequestStatus } from '@/types/template-request';
import { FileText, ExternalLink, Check, X, Loader2 } from 'lucide-react';
import { timestamp_to_date } from '@/utils/time';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { CreateTemplateRequestDialog } from './create-dialog';
import { DataTable } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import DataTableSkeleton from '@/components/ui/data-table-skeleton';
import { PermissionGuard } from '@/components/permission-guard';
import { UserPermission } from '@/constants/permissions';

export function MyTemplateRequestsList() {
    const { t } = useTranslation();
    const [requests, setRequests] = useState<TemplateRequest[]>([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [params, setParams] = useState({
        page: 0,
        page_size: 10,
    });

    // Reject Dialog State
    const [rejectDialog, setRejectDialog] = useState<{ open: boolean, id: string | null }>({
        open: false,
        id: null
    });
    const [rejectReason, setRejectReason] = useState('');
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const handleApprove = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setActionLoading(id);
        try {
            await update_template_request_status(id, { status: 'done' });
            toast({
                title: t('success'),
                description: t('request_approved', 'Request approved'),
            });
            fetchRequests();
        } catch (error) {
            toast({
                title: t('error'),
                description: t('error_updating_request', 'Failed to update request'),
                variant: 'destructive',
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        if (!rejectDialog.id) return;

        setActionLoading(rejectDialog.id);
        try {
            await update_template_request_status(rejectDialog.id, {
                status: 'refused',
                admin_notes: rejectReason
            });
            toast({
                title: t('success'),
                description: t('request_rejected', 'Request rejected'),
            });
            setRejectDialog({ open: false, id: null });
            setRejectReason('');
            fetchRequests();
        } catch (error) {
            toast({
                title: t('error'),
                description: t('error_updating_request', 'Failed to update request'),
                variant: 'destructive',
            });
        } finally {
            setActionLoading(null);
        }
    };

    const fetchRequests = async () => {
        setLoading(true);
        setError(false);
        try {
            const data = await list_my_requests({
                limit: params.page_size,
                offset: params.page * params.page_size
            });
            setRequests(data.results);
            setCount(data.count);
        } catch (error) {
            console.error('Failed to fetch requests', error);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [params]);

    const getStatusBadge = (status: TemplateRequestStatus) => {
        switch (status) {
            case 'done':
                return <Badge className="bg-green-500">{t('status_done')}</Badge>;
            case 'refused':
                return <Badge variant="destructive">{t('status_refused')}</Badge>;
            default:
                return <Badge variant="secondary">{t('status_pending')}</Badge>;
        }
    };

    const columns: ColumnDef<TemplateRequest>[] = [
        {
            accessorKey: 'created_at',
            header: t('date'),
            cell: ({ row }) => format(timestamp_to_date(row.original.created_at), 'MMM dd, yyyy'),
        },
        {
            accessorKey: 'filename',
            header: t('filename'),
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate max-w-[200px]" title={row.original.filename}>
                        {row.original.filename}
                    </span>
                </div>
            ),
        },
        {
            accessorKey: 'status',
            header: t('status'),
            cell: ({ row }) => getStatusBadge(row.original.status),
        },
        {
            accessorKey: 'admin_notes',
            header: t('notes'),
            cell: ({ row }) => (
                <span className="text-sm text-muted-foreground truncate max-w-[200px] block" title={row.original.admin_notes}>
                    {row.original.admin_notes || '-'}
                </span>
            ),
        },
        {
            id: 'actions',
            header: t('actions'),
            cell: ({ row }) => (
                <div className="flex items-center justify-end gap-2 text-right">
                    <Button variant="ghost" size="icon" asChild title={t('download')} onClick={(e) => e.stopPropagation()}>
                        <a href={row.original.file_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                            <span className="sr-only">{t('download')}</span>
                        </a>
                    </Button>

                    {row.original.status === 'pending' && (
                        <>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={(e) => handleApprove(row.original.id, e)}
                                disabled={actionLoading === row.original.id}
                                title={t('approve')}
                            >
                                {actionLoading === row.original.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Check className="h-4 w-4" />
                                )}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setRejectDialog({ open: true, id: row.original.id });
                                }}
                                disabled={actionLoading === row.original.id}
                                title={t('reject')}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </>
                    )}
                </div>
            ),
        },
    ];

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle>{t('my_template_requests')}</CardTitle>
                    <CardDescription>
                        {t('my_template_requests_desc')}
                    </CardDescription>
                </div>
                <PermissionGuard permission={UserPermission.ADD_TEMPLATE_REQUEST}>
                    <CreateTemplateRequestDialog
                        trigger={<Button>{t('new_request')}</Button>}
                        onSuccess={fetchRequests}
                    />
                </PermissionGuard>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <DataTableSkeleton columns={5} rows={5} />
                ) : error ? (
                    <div className='py-8 text-center text-destructive'>
                        {t('error_fetching_requests')}
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={requests}
                        pages={Math.ceil(count / params.page_size)}
                        params={params}
                        setParams={setParams}
                        showPagination={true}
                    />
                )}
            </CardContent>

            <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, id: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('reject_request', 'Reject Request')}</DialogTitle>
                        <DialogDescription>
                            {t('reject_reason_desc', 'Please provide a reason')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={t('reason_placeholder', 'Reason')}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialog({ open: false, id: null })}>
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleReject}
                            disabled={!rejectReason || !!actionLoading}
                        >
                            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            {t('reject')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
